import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	OnDestroy,
	OnChanges,
	SimpleChanges,
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription, Subject, ReplaySubject, Observable, of, BehaviorSubject, throwError } from "rxjs";
import { auditTime, catchError, filter, map, shareReplay, startWith, switchMap, tap } from "rxjs/operators";
import { PersonaGiuridicaDTO } from "../../models/persona-giuridica-dto";
import { PersonaGiuridica } from "../../models/portale/persona-giuridica-model";
import { Sede } from "../../models/portale/sede";
import { ComponentOutputStatus } from "../../normalizzazione/component-output-status";
import { DelegheService } from "../../services/deleghe-services/deleghe.service";
import { MessageService } from "../../services/message.service";
import { ComponentReducer } from "../../utils/component-reducer";
import { given, is } from "../../utils/fnutils";
import { MatDialog } from '@angular/material/dialog';
import { GenericModalComponent } from '../../common-component/generic-modal/generic-modal.component';
import { Azione, GenericModalData } from '../../common-component/generic-modal/generic-modal-data';
import { ValidationService } from "../../services/validation.service";


interface infoPersonaGiuridica {
	codiceFiscale: string,
	pIva: string,
	ragioneSociale: string
}

class PgValidation {
	constructor(public valid: boolean, public pg: PersonaGiuridica) {}
}

@Component({
	selector: "lib-persona-giuridica",
	templateUrl: "./persona-giuridica.component.html",
	styleUrls: ["./persona-giuridica.component.css"],
})
export class PersonaGiuridicaComponent implements OnInit, OnDestroy, OnChanges {

	@Input()
	cf$: ReplaySubject<string>;

	@Input()
	datisocieta: PersonaGiuridicaDTO;

	@Input()
	idSedeSelezionata: string;

	@Input()
	namespace: string;

	@Input()
	resetForm: Subject<boolean>;

	@Input()
	validatorFormControl: boolean = false;

	@Input()
	gestioneSedi: boolean;

	@Input()
	couldBeDittaIndividuale: boolean = true;

	@Input()
	personaGiuridicaFromDeleghe$: Observable<PersonaGiuridica>

	@Output()
	done: EventEmitter<
		ComponentOutputStatus<PersonaGiuridicaDTO>
	> = new EventEmitter<ComponentOutputStatus<PersonaGiuridicaDTO>>();

	subscription: Subscription;
	subValidation: Subscription;
	personaGiuridica$: Observable<PgValidation>;
	changeSedi$: ReplaySubject<ComponentOutputStatus<(value: PersonaGiuridicaDTO) => void>> = new ReplaySubject<
		ComponentOutputStatus<(value: PersonaGiuridicaDTO) => void>
	>(1);
	changePG$: ReplaySubject<ComponentOutputStatus<(value: PersonaGiuridicaDTO) => void>> = new ReplaySubject<
		ComponentOutputStatus<(value: PersonaGiuridicaDTO) => void>
	>(1);
	$changePG: Subscription;
	datiSocieta$: Observable<PersonaGiuridicaDTO>;
	streamListaSedi$: ReplaySubject<Array<Sede>>;
	personaSelected$: BehaviorSubject<PersonaGiuridica> =
		new BehaviorSubject<PersonaGiuridica>(null)
	$sedi: Subscription;
	subCF: Subscription;
	form: FormGroup;
	CODICE_LEGALE = "LEGALE";
	errorSedi$: Subject<string> = new Subject<string>();
	invalidPgsSubscriptions: Subscription;

	constructor(
		private msg: MessageService,
		private delegheService: DelegheService,
		protected dialog: MatDialog
	) { }

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.subValidation?.unsubscribe();
		this.subCF?.unsubscribe();
		this.invalidPgsSubscriptions?.unsubscribe();
	}

	ngOnInit() {

		//GESTIONE_SEDI
		this.streamListaSedi$ = new ReplaySubject();
		this.changeSedi$.next(ComponentOutputStatus.of(!this.gestioneSedi, (pg: PersonaGiuridicaDTO) => pg));

		//SE_INPUT_DATI_SOSCIETA_ESISTE => RECUPERO LE SEDI
		this.datisocieta?.sedi ?
			this.streamListaSedi$.next(this.datisocieta?.sedi) :
			null
		//VALIDAZIONE_INIZIALE
		const valid = false;

		this.form = new FormGroup({});

		if (this.resetForm) {
			this.subscription = this.resetForm.subscribe(val =>
				val ? this.pulisciForm() : "");
		}

		if (!this.datisocieta) {
			this.initDatiSocieta();
		}

		const codiceFiscale = new FormControl(
			{ value: this.datisocieta.codiceFiscale, disabled: this.personaGiuridicaFromDeleghe$ == null ? false : true },
			[
				this.validatorFormControl ? Validators.required : Validators.nullValidator,
				ValidationService.pattern(
					this.couldBeDittaIndividuale ? ValidationService._codiceFiscalePiva : ValidationService.partitaIva,
					"cfInvalidoFormalmente"
				),
			],

		);
		this.form.addControl("codiceFiscale", codiceFiscale);

		const ragioneSociale = new FormControl(
			{ value: this.datisocieta.ragioneSociale, disabled: true },
			[
				// this.validatorFormControl ? Validators.required : Validators.nullValidator
			]
		);
		this.form.addControl("ragioneSociale", ragioneSociale);

		const partitaIVA = new FormControl(
			{ value: this.datisocieta.partitaIVA, disabled: true },
			[
				// this.validatorFormControl ? Validators.required : Validators.nullValidator,
				// ValidationService.pattern(
				// 	ValidationService.partitaIva,
				// 	"partitaIVAInvalida"
				// ),
			]
		);
		this.form.addControl("partitaIVA", partitaIVA);

		if (this.personaGiuridicaFromDeleghe$) {
			this.personaGiuridica$ = this.personaGiuridicaFromDeleghe$
				.pipe(
					map(pg => new PgValidation(true, pg)),
					catchError(_ =>  of(new PgValidation(false, null)) ),
					shareReplay()
				)
		} else {
			this.personaGiuridica$ = this.form.get('codiceFiscale').valueChanges
				.pipe(
					auditTime(1000),
					switchMap(value => {
						return ((this.couldBeDittaIndividuale && (value?.length == 11 || value?.length == 16))
						        || 
								(!this.couldBeDittaIndividuale && value?.length == 11)
						       )
						 ?					
							this.delegheService.getPersonaGiuridicaCodiceFiscale(value)
								.pipe(
									map(pg => new PgValidation(true, pg)),
									catchError(_ => this.catchPersonaGiuridicaError())
								) :
						 	of(new PgValidation(false, null))
					}),
					shareReplay(),
					tap((validationResult) => {
						console.log('[validationResult]', validationResult);
					}),
					shareReplay()
				);
		}

		const validPgs$ = this.personaGiuridica$.pipe(
			filter(v => v.valid),
			map(v => v.pg)
		)
		
		const invalidPgs$ = this.personaGiuridica$.pipe(
			filter(pg => !pg.valid)
		); 

		//EVOLUZIONE_PG
		const pg$ = this.buildStreamChangePG$(validPgs$);
		const pgChange$ = pg$.pipe(
			tap(pg => this.setSedi(pg['sedi'])),
			tap(pg => this.controlloSedeLegale(pg)),
			map(pg => { 
				
				const validity = pg['sedi']?.find(sede => sede.tipoSede['codice'] == this.CODICE_LEGALE) ? 
					!this.form.invalid
					: false;

				return ComponentOutputStatus.of(
					validity
					, (personaGiuridica) => Object.keys(pg)
						.forEach(key => personaGiuridica[key] = pg[key])
					)
			})
		);

		//___Validazione___
		const changes$ = ComponentReducer
			.reducer2(
				pgChange$,
				this.changeSedi$,
				_ => true,
				ComponentOutputStatus.of(valid, this.datisocieta)
			);

		//____OUTPUT_COMPONENT____
		this.subscription = changes$.subscribe((_) => this.done.emit(_));

		this.invalidPgsSubscriptions = invalidPgs$.subscribe(
			_ => {
				this.form.get('partitaIVA').setValue("");
				this.form.get('ragioneSociale').setValue("");
				this.personaSelected$.next(null);
				this.done.emit(ComponentOutputStatus.of(false, new PersonaGiuridicaDTO()));
			}
		)

		if (this.datisocieta) {
			codiceFiscale.setValue(this.datisocieta?.codiceFiscale);
		}

		//CF_DALL'ESTERNO
		if (this.cf$) {
			this.subCF = this.cf$
				.pipe(tap(cf => codiceFiscale.setValue(cf)))
				.subscribe();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		console.log('ngOnChanges:',changes);
		if(this.form?.controls?.codiceFiscale) {
			this.form?.controls?.codiceFiscale.setValidators(null);
			this.form?.controls?.codiceFiscale.setValidators([
				this.validatorFormControl ? Validators.required : Validators.nullValidator,
				ValidationService.pattern(
					this.couldBeDittaIndividuale ? ValidationService._codiceFiscalePiva : ValidationService.partitaIva,
					"cfInvalidoFormalmente"
				),
			]);

			if(changes?.couldBeDittaIndividuale?.currentValue == true || changes?.couldBeDittaIndividuale?.currentValue == false)
			{
				if((changes?.couldBeDittaIndividuale?.currentValue != changes?.couldBeDittaIndividuale?.previousValue) || changes?.couldBeDittaIndividuale?.previousValue === undefined){
					this.form?.controls?.codiceFiscale?.patchValue(null);
				}
			}
				
			//this.form?.controls?.codiceFiscale?.updateValueAndValidity();
		}
	}

	private catchPersonaGiuridicaError(): Observable<PgValidation> {

		this.dialog.open(
			GenericModalComponent,
			{ data:
				{ title: 'persona-giuridica-by-cf-error-titolo'
				, azioni: [ new Azione('ok') ]
				, paragrafi: [ 'modalita-pagamento-dialog-testo']
				} as GenericModalData
			, panelClass: 'custom-modalbox-ko'
			}
		);

		return of(new PgValidation(false, null));
	  }

	controlloSedeLegale(pg) {
		const sedeLegale = pg['sedi']?.find(sede => sede.tipoSede['codice'] == this.CODICE_LEGALE);
		let error: string
		if (pg['sedi']?.length == 0 || !pg['sedi']) {
			error = "nessunaSede"
		} else if (!sedeLegale) {
			error = "nessunaSedeLegale"
		}
		this.errorSedi$.next(error);
	}
	buildStreamChangePG$(personaGiuridica$: Observable<PersonaGiuridica>) {
		return personaGiuridica$
			.pipe(
				tap(pg => this.personaSelected$.next(pg)),
				map(pg => {
					return {
						codiceFiscale: pg.datiPersonaGiuridica.codiceFiscale,
						partitaIVA: pg.datiPersonaGiuridica.partitaIVA,
						ragioneSociale: pg.datiPersonaGiuridica.ragioneSociale,
						idPersonaGiuridica: pg.datiPersonaGiuridica.id,
						codiceRegione: pg?.sedi
							?.filter(sedeLegale =>
								sedeLegale?.tipoSede?.codice === "LEGALE")[0]?.indirizzo?.regione?.sigla,
						sedi: pg.sedi,
						hasAmmUtenze: pg.hasAmmUtenze
					} as PersonaGiuridicaDTO
				}),
				tap(pg => {
					this.form.get('partitaIVA').setValue(pg['partitaIVA']);
					this.form.get('ragioneSociale').setValue(pg['ragioneSociale']);
				}),
				startWith(ComponentOutputStatus.of(false, pg => pg))
			)

	}

	setSedi(sedi: Array<Sede>) {
		(given(sedi)
			.when(is(null), () => this.streamListaSedi$.next([]))
			.otherwise(() => this.streamListaSedi$.next(sedi))
			.valueOf() as Function
		)();
	}

	sediReady(sedeSelectedStatus: ComponentOutputStatus<Array<Sede>>) {
		// console.log("[sedeSelectedStatus]", sedeSelectedStatus);
		this.changeSedi$.next(
			sedeSelectedStatus.mapValue(
				(sede: Array<Sede>) => (pg: PersonaGiuridicaDTO) => pg.sedi = sede
			)
		)
	}

	initDatiSocieta() {
		this.datisocieta = new PersonaGiuridicaDTO();
	}

	pulisciForm() {
		const keys = Object.keys(this.form.value);
		keys.forEach(key => {
			const control: AbstractControl = this.form.get(key);
			control.setValue("");
		});
	}
}
